import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { classeService, Classe, PageResponse, ClasseSearchRequest, ClasseSearchFilters } from '../../services/classeService';
import { levelService, Level } from '../../services/levelService';
import ScoreInsertModal from '../../components/ScoreInsertModal';
import { useRights } from '../../hooks/useRights';
import { useHasAnyActionRight, classeActions } from '../../utils/actionRightsUtils';
import { useModal } from '../../context/ModalContext';
import { Pencil, Trash2, ClipboardList, FileText } from 'lucide-react';
import '../../styles/index.css';
import TableActions from '../../components/TableActions';

const Classes: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasRight } = useRights();
  const { showConfirm, showAlert } = useModal();
  const hasAnyActionRight = useHasAnyActionRight(classeActions());
  const [classes, setClasses] = useState<Classe[]>([]);
  const [levels, setLevels] = useState<Level[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalElements, setTotalElements] = useState(0);
  const [isDeepSearch, setIsDeepSearch] = useState(false);
  const observerTarget = useRef<HTMLDivElement>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [filters, setFilters] = useState<ClasseSearchFilters>({
    name: '',
    levelId: '',
  });
  const [editingClasse, setEditingClasse] = useState<Classe | null>(null);
  const [editForm, setEditForm] = useState({
    levelId: '',
    name: '',
    academicYear: '',
  });
  const [isCreating, setIsCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    levelId: '',
    name: '',
    academicYear: '',
  });

  // Score insertion modal
  const [isScoreModalOpen, setIsScoreModalOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState<Classe | null>(null);

  useEffect(() => {
    loadClasses(0, true);
    loadLevels();
  }, []);

  const loadLevels = async () => {
    try {
      const data = await levelService.getAllLevels();
      setLevels(data);
    } catch (e: any) {
      console.error('Failed to load levels:', e);
    }
  };

  const loadClasses = async (page: number = 0, reset: boolean = true) => {
    try {
      if (reset) {
        setLoading(true);
        setCurrentPage(0);
        setIsDeepSearch(false);
      } else {
        setLoadingMore(true);
      }

      const searchRequest: ClasseSearchRequest = {
        page,
        size: 100,
        filters: isDeepSearch ? filters : undefined,
      };

      const response: PageResponse<Classe> = await classeService.searchClasses(searchRequest);

      if (reset) {
        setClasses(response.content || []);
      } else {
        setClasses(prev => [...(prev || []), ...(response.content || [])]);
      }

      setCurrentPage(response.page);
      setHasMore(response.hasNext);
      setTotalElements(response.totalElements);
      setError('');
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to load classes');
      console.error('Load classes error:', e);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreClasses = useCallback(() => {
    if (!loadingMore && hasMore && !loading) {
      setLoadingMore(true);
      const searchFilters: ClasseSearchFilters = {
        name: filters.name || undefined,
        levelId: filters.levelId || undefined,
      };

      const searchRequest: ClasseSearchRequest = {
        page: currentPage + 1,
        size: 100,
        filters: isDeepSearch ? searchFilters : undefined,
      };

      classeService.searchClasses(searchRequest).then(response => {
        setClasses(prev => [...(prev || []), ...(response.content || [])]);
        setCurrentPage(response.page);
        setHasMore(response.hasNext);
        setTotalElements(response.totalElements);
        setLoadingMore(false);
      }).catch(e => {
        setError(e.response?.data?.message || 'Failed to load more classes');
        setLoadingMore(false);
      });
    }
  }, [currentPage, hasMore, loadingMore, loading, isDeepSearch, filters]);

  const handleDeepSearch = async () => {
    try {
      setLoading(true);
      setIsDeepSearch(true);
      setCurrentPage(0);

      const searchFilters: ClasseSearchFilters = {
        name: filters.name || undefined,
        levelId: filters.levelId || undefined,
      };

      const searchRequest: ClasseSearchRequest = {
        page: 0,
        size: 100,
        filters: searchFilters,
      };

      const response: PageResponse<Classe> = await classeService.searchClasses(searchRequest);
      setClasses(response.content || []);
      setCurrentPage(response.page);
      setHasMore(response.hasNext);
      setTotalElements(response.totalElements);
      setError('');
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to search classes');
      console.error('Deep search error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const currentTarget = observerTarget.current;
    if (!currentTarget) return;

    const handleScroll = () => {
      const container = currentTarget;
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      if (scrollHeight - scrollTop - clientHeight < 100 && hasMore && !loadingMore && !loading) {
        loadMoreClasses();
      }
    };

    currentTarget.addEventListener('scroll', handleScroll);

    return () => {
      currentTarget.removeEventListener('scroll', handleScroll);
    };
  }, [loadMoreClasses, hasMore, loadingMore, loading]);

  const filteredClasses = isDeepSearch ? (classes || []) : (classes || []).filter((classe) => {
    if (filters.name && !classe.name?.toLowerCase().includes(filters.name.toLowerCase())) {
      return false;
    }
    if (filters.levelId && classe.levelId !== filters.levelId) {
      return false;
    }
    return true;
  });

  const handleFilterChange = (key: keyof ClasseSearchFilters, value: string | undefined) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setIsDeepSearch(false);
  };

  const clearFilters = () => {
    setFilters({
      name: '',
      levelId: '',
    });
    setIsDeepSearch(false);
    loadClasses(0, true);
  };

  const handleEdit = (classe: Classe) => {
    setEditingClasse(classe);
    setEditForm({
      levelId: classe.levelId || '',
      name: classe.name || '',
      academicYear: classe.academicYear || '',
    });
  };

  const handleCancelEdit = () => {
    setEditingClasse(null);
    setEditForm({
      levelId: '',
      name: '',
      academicYear: '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editingClasse) return;
    if (!editForm.name.trim()) {
      setError(t('classes.nameRequired'));
      return;
    }

    try {
      await classeService.updateClasse(editingClasse.id!, {
        levelId: editForm.levelId,
        name: editForm.name,
        academicYear: editForm.academicYear,
      });
      await loadClasses(0, true);
      handleCancelEdit();
      setError('');
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to update class');
      console.error('Update class error:', e);
    }
  };

  const handleDelete = (id: string) => {
    showConfirm(t('common.delete'), t('classes.deleteConfirm'), async () => {
      try {
        await classeService.deleteClasse(id);
        await loadClasses();
        setError('');
      } catch (e: any) {
        setError(e.response?.data?.message || 'Failed to delete class');
        showAlert(t('common.error'), e.response?.data?.message || 'Failed to delete class');
        console.error('Delete class error:', e);
      }
    });
  };

  const handleStartCreate = () => {
    setIsCreating(true);
    setCreateForm({
      levelId: '',
      name: '',
      academicYear: '',
    });
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setCreateForm({
      levelId: '',
      name: '',
      academicYear: '',
    });
  };

  const handleSaveCreate = async () => {
    if (!createForm.name.trim()) {
      setError(t('classes.nameRequired'));
      return;
    }

    try {
      await classeService.createClasse({
        levelId: createForm.levelId,
        name: createForm.name,
        academicYear: createForm.academicYear,
      });
      await loadClasses(0, true);
      handleCancelCreate();
      setError('');
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to create class');
      console.error('Create class error:', e);
    }
  };

  const getLevelName = (levelId: string) => {
    const level = levels.find(l => l.id === levelId);
    return level ? level.name : levelId;
  };

  if (loading && classes.length === 0) {
    return <div className="bo-container">{t('common.loading')}</div>;
  }

  return (
    <div className="bo-container">
      <div className="bo-header">
        <div className="header-left">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="btn-toggle-sidebar"
            title={sidebarOpen ? t('common.hideFilters') : t('common.showFilters')}
          >
            {sidebarOpen ? '◀' : '▶'}
          </button>
          <h2>{t('classes.title')}</h2>
        </div>
        {hasRight('classes:c') && (
          <button className="btn-create" onClick={handleStartCreate}>
            {t('classes.createNew')}
          </button>
        )}
      </div>

      <div className="bo-content-wrapper">
        {sidebarOpen && (
          <div className="bo-sidebar">
            <div className="sidebar-header">
              <h3>{t('classes.filters')}</h3>
              <div className="sidebar-header-actions">
                <button onClick={handleDeepSearch} className="btn-deep-search">
                  {t('classes.deepSearch')}
                </button>
                <button onClick={clearFilters} className="btn-clear-filters">
                  {t('common.clear')}
                </button>
              </div>
            </div>
            {isDeepSearch && (
              <div className="deep-search-indicator">
                <small>{t('classes.deepSearchActive')} ({totalElements} {t('classes.results')})</small>
              </div>
            )}
            <div className="sidebar-body">
              <div className="filter-group">
                <label>{t('classes.name')}</label>
                <input
                  type="text"
                  value={filters.name || ''}
                  onChange={(e) => handleFilterChange('name', e.target.value)}
                  placeholder={t('classes.filterByName')}
                />
              </div>
              <div className="filter-group">
                <label>{t('classes.levelId')}</label>
                <select
                  value={filters.levelId || ''}
                  onChange={(e) => handleFilterChange('levelId', e.target.value)}
                >
                  <option value="">{t('classes.allLevels')}</option>
                  {levels.map((level) => (
                    <option key={level.id} value={level.id}>
                      {level.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        <div className={`bo-main-content ${!sidebarOpen ? 'full-width' : ''}`}>
          {error && <div className="error-message">{error}</div>}

          {isCreating && (
            <div className="modal-overlay" onClick={handleCancelCreate}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h3>{t('classes.createNew')}</h3>
                <div className="form-group">
                  <div className="form-field">
                    <label>{t('classes.levelId')} *</label>
                    <select
                      value={createForm.levelId}
                      onChange={(e) => setCreateForm({ ...createForm, levelId: e.target.value })}
                    >
                      <option value="">{t('classes.levelIdPlaceholder')}</option>
                      {levels.map((level) => (
                        <option key={level.id} value={level.id}>
                          {level.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>{t('classes.name')} *</label>
                    <input
                      type="text"
                      value={createForm.name}
                      onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                      placeholder={t('classes.namePlaceholder')}
                    />
                  </div>
                  <div className="form-field">
                    <label>{t('classes.academicYear')}</label>
                    <input
                      type="text"
                      value={createForm.academicYear}
                      onChange={(e) => setCreateForm({ ...createForm, academicYear: e.target.value })}
                      placeholder="2023-2024"
                    />
                  </div>
                </div>
                <div className="modal-actions">
                  <button className="btn-cancel" onClick={handleCancelCreate}>
                    {t('common.cancel')}
                  </button>
                  <button className="btn-save" onClick={handleSaveCreate}>
                    {t('common.save')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {editingClasse && (
            <div className="modal-overlay" onClick={handleCancelEdit}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h3>{t('classes.edit')}</h3>
                <div className="form-group">
                  <div className="form-field">
                    <label>{t('classes.levelId')} *</label>
                    <select
                      value={editForm.levelId}
                      onChange={(e) => setEditForm({ ...editForm, levelId: e.target.value })}
                    >
                      <option value="">{t('classes.levelIdPlaceholder')}</option>
                      {levels.map((level) => (
                        <option key={level.id} value={level.id}>
                          {level.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>{t('classes.name')} *</label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    />
                  </div>
                  <div className="form-field">
                    <label>{t('classes.academicYear')}</label>
                    <input
                      type="text"
                      value={editForm.academicYear}
                      onChange={(e) => setEditForm({ ...editForm, academicYear: e.target.value })}
                      placeholder="2023-2024"
                    />
                  </div>
                </div>
                <div className="modal-actions">
                  <button className="btn-cancel" onClick={handleCancelEdit}>
                    {t('common.cancel')}
                  </button>
                  <button className="btn-save" onClick={handleSaveEdit}>
                    {t('common.save')}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="bo-table-container">
            {filteredClasses.length === 0 && !loading ? (
              <div className="no-data-message">
                <p>{t('classes.noClassesFound')}</p>
              </div>
            ) : (
              <>
                <div className="bo-table-wrapper" ref={observerTarget}>
                  <table className="bo-table">
                    <thead>
                      <tr>
                        <th>{t('classes.name')}</th>
                        <th>{t('classes.levelId')}</th>
                        <th>{t('classes.academicYear')}</th>
                        {hasAnyActionRight && <th>{t('common.actions')}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredClasses.map((classe) => (
                        <tr key={classe.id}>
                          <td>{classe.name}</td>
                          <td>{getLevelName(classe.levelId)}</td>
                          <td>{classe.academicYear || '-'}</td>
                          {hasAnyActionRight && (
                            <td>
                              {hasAnyActionRight && (
                                <td>
                                  <TableActions actions={[
                                    ...(hasRight('classes:u') ? [{
                                      label: t('common.edit'),
                                      onClick: () => handleEdit(classe),
                                      className: 'btn-edit',
                                      icon: <Pencil size={16} />,
                                      hideLabelDesktop: true
                                    }] : []),
                                    ...(hasRight('classes:d') ? [{
                                      label: t('common.delete'),
                                      onClick: () => handleDelete(classe.id!),
                                      className: 'btn-delete',
                                      icon: <Trash2 size={16} />,
                                      hideLabelDesktop: true,
                                      variant: 'danger' as const
                                    }] : []),
                                    ...(hasRight('scores:c') ? [{
                                      label: t('classes.insertScores'),
                                      onClick: () => {
                                        setSelectedClass(classe);
                                        setIsScoreModalOpen(true);
                                      },
                                      className: 'btn-scores',
                                      icon: <ClipboardList size={16} />,
                                      hideLabelDesktop: true
                                    }] : []),
                                    ...(hasRight('bulletins:c') ? [{
                                      label: t('classes.bulletin'),
                                      onClick: () => navigate(`/bulletin/${classe.id}`),
                                      className: 'btn-bulletin',
                                      icon: <FileText size={16} />,
                                      hideLabelDesktop: true
                                    }] : [])
                                  ]} />
                                </td>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="scroll-trigger">
                    {loadingMore && (
                      <div className="loading-more">
                        <small>{t('common.loading')}...</small>
                      </div>
                    )}
                    {!hasMore && classes.length > 0 && (
                      <div className="no-more-data">
                        <small>{t('classes.allLoaded')}</small>
                      </div>
                    )}
                  </div>
                </div>
                {!isDeepSearch && (
                  <div className="table-info">
                    <small>{t('classes.showingLoaded')} ({classes.length} {t('classes.loaded')})</small>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Score Insert Modal */}
      <ScoreInsertModal
        isOpen={isScoreModalOpen}
        onClose={() => {
          setIsScoreModalOpen(false);
          setSelectedClass(null);
        }}
        classId={selectedClass?.id || ''}
        className={selectedClass?.name || ''}
      />
    </div>
  );
};

export default Classes;
